import { Download, Share, Smartphone, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type Platform = 'ios' | 'android' | 'other';

function detectPlatform(): Platform {
  const agent = navigator.userAgent.toLocaleLowerCase();
  const ios = /iphone|ipad|ipod/.test(agent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (ios) return 'ios';
  if (/android/.test(agent)) return 'android';
  return 'other';
}

export function InstallButton() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [promptEvent, setPromptEvent] = useState<InstallPromptEvent | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [platform, setPlatform] = useState<Platform>(() => detectPlatform());
  const [installed, setInstalled] = useState(() =>
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone),
  );

  useEffect(() => {
    const handlePrompt = (event: Event) => {
      event.preventDefault();
      setPromptEvent(event as InstallPromptEvent);
    };
    const handleInstalled = () => {
      setInstalled(true);
      setPromptEvent(null);
    };
    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('appinstalled', handleInstalled);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => undefined);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (showHelp && !dialog.open) dialog.showModal();
    if (!showHelp && dialog.open) dialog.close();
  }, [showHelp]);

  async function installOrExplain() {
    if (installed) return;
    if (!promptEvent) {
      setPlatform(detectPlatform());
      setShowHelp(true);
      return;
    }
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice.outcome === 'accepted') setInstalled(true);
    setPromptEvent(null);
  }

  return (
    <>
      <button
        className="install-trigger"
        type="button"
        onClick={() => void installOrExplain()}
        disabled={installed}
      >
        <Smartphone aria-hidden="true" />
        {installed ? 'On your phone' : 'Add to phone'}
      </button>

      <dialog
        ref={dialogRef}
        className="install-dialog"
        aria-labelledby="install-title"
        onCancel={() => setShowHelp(false)}
      >
        <div className="dialog-card">
          <button className="icon-button dialog-close" type="button" onClick={() => setShowHelp(false)} aria-label="Close">
            <X aria-hidden="true" />
          </button>
          <span className="dialog-icon" aria-hidden="true"><Download /></span>
          <p className="eyebrow">ONE TAP AWAY</p>
          <h2 id="install-title">Add this app to your home screen</h2>

          {platform === 'ios' ? (
            <ol>
              <li>Open this page in <strong>Safari</strong>.</li>
              <li>Tap Safari’s <Share aria-hidden="true" /> <strong>Share</strong> button.</li>
              <li>Tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</li>
            </ol>
          ) : platform === 'android' ? (
            <ol>
              <li>Open this page in <strong>Chrome</strong>.</li>
              <li>Tap Chrome’s three-dot menu.</li>
              <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
            </ol>
          ) : (
            <ol>
              <li>Open your browser menu.</li>
              <li>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
              <li>On iPhone, use Safari’s Share button.</li>
            </ol>
          )}

          <p className="dialog-note">No app store needed. It opens like any other app.</p>
          <button className="primary-button dialog-done" type="button" onClick={() => setShowHelp(false)}>Got it</button>
        </div>
      </dialog>
    </>
  );
}
